import type { ConversationRecordReference, SalesforceContext } from "@ai-sales/contracts";
import { LayersIcon } from "./icons.js";
import {
  describeSalesforceContext,
  salesforceObjectLabel,
  type ContextSourceMode
} from "./salesforce-context.js";

interface ConversationContextPanelProps {
  liveContext: Partial<SalesforceContext>;
  conversationContext: Partial<SalesforceContext>;
  references: ConversationRecordReference[];
  mode: ContextSourceMode;
  pageContextAvailable?: boolean;
  busy: boolean;
  onModeChange: (mode: ContextSourceMode) => void;
  onSelectReference: (reference: ConversationRecordReference) => Promise<void>;
  onClearConversationContext: () => Promise<void>;
  onClose: () => void;
}

export function ConversationContextPanel(props: ConversationContextPanelProps) {
  const pageContextAvailable = props.pageContextAvailable ?? true;
  const pageDescription = describeSalesforceContext(props.liveContext);
  const conversationDescription = describeSalesforceContext(props.conversationContext);
  const hasConversationFocus = Boolean(
    props.conversationContext.recordId || props.conversationContext.objectApiName
  );
  const references = uniqueReferences(props.references);

  return (
    <aside className="action-center context-manager" aria-label="上下文管理">
      <header>
        <div>
          <strong>上下文管理</strong>
          <span>明确选择本轮对话使用 Salesforce 当前页面，还是会话中的业务记录</span>
        </div>
        <button type="button" aria-label="关闭上下文管理" onClick={props.onClose}>×</button>
      </header>
      <div className="context-manager__body">
        <section className="context-source-picker" aria-label="上下文来源">
          <button
            type="button"
            className={props.mode === "page" ? "context-source-card context-source-card--active" : "context-source-card"}
            aria-pressed={props.mode === "page"}
            disabled={!pageContextAvailable}
            onClick={() => props.onModeChange("page")}
          >
            <span className="context-source-card__icon"><LayersIcon size={18} /></span>
            <span>
              <strong>{pageContextAvailable ? "跟随 Salesforce 当前页面" : "独立 Web 无当前页面"}</strong>
              <small>{pageContextAvailable
                ? `${pageDescription.objectLabel} · ${pageDescription.contextLabel}`
                : "通过会话记录管理业务上下文"}</small>
            </span>
            <b>{pageContextAvailable ? props.mode === "page" ? "使用中" : "使用" : "不可用"}</b>
          </button>
          <button
            type="button"
            className={props.mode === "conversation" ? "context-source-card context-source-card--active" : "context-source-card"}
            aria-pressed={props.mode === "conversation"}
            disabled={!hasConversationFocus}
            onClick={() => props.onModeChange("conversation")}
          >
            <span className="context-source-card__icon"><LayersIcon size={18} /></span>
            <span>
              <strong>使用当前会话记录</strong>
              <small>{hasConversationFocus
                ? `${conversationDescription.objectLabel} · ${conversationDescription.contextLabel}`
                : "当前会话尚未选择业务记录"}</small>
            </span>
            <b>{props.mode === "conversation" ? "使用中" : "使用"}</b>
          </button>
        </section>

        <section className="context-reference-list">
          <div className="context-manager__section-heading">
            <div>
              <strong>本会话涉及的记录</strong>
              <span>来源包括页面上下文、查询结果和已执行操作</span>
            </div>
            {hasConversationFocus ? (
              <button
                type="button"
                disabled={props.busy}
                onClick={() => void props.onClearConversationContext()}
              >清除会话记录</button>
            ) : null}
          </div>
          {references.length ? references.map((reference) => (
            <button
              type="button"
              className="context-reference"
              key={`${reference.recordId}:${reference.source}`}
              aria-label={`将 ${reference.recordLabel ?? reference.recordId} 设为会话上下文`}
              disabled={props.busy}
              onClick={() => void props.onSelectReference(reference)}
            >
              <span>{salesforceObjectLabel(reference.objectApiName).slice(0, 2)}</span>
              <span>
                <strong>{reference.recordLabel ?? reference.recordId}</strong>
                <small>{salesforceObjectLabel(reference.objectApiName)} · {referenceSourceLabel(reference.source)}</small>
              </span>
              <b>设为会话上下文</b>
            </button>
          )) : (
            <p className="context-reference-list__empty">查询或执行 Salesforce 记录后，会在这里保留可继续使用的上下文。</p>
          )}
        </section>
      </div>
    </aside>
  );
}

function uniqueReferences(references: ConversationRecordReference[]): ConversationRecordReference[] {
  const seen = new Set<string>();
  return references.filter((reference) => {
    if (seen.has(reference.recordId)) return false;
    seen.add(reference.recordId);
    return true;
  });
}

function referenceSourceLabel(source: ConversationRecordReference["source"]): string {
  if (source === "action_result") return "操作结果";
  if (source === "user_selected") return "用户选择";
  return "Salesforce 页面";
}
